// src/currency.ts
var CURRENCY_LOCALE = {
  INR: "en-IN",
  USD: "en-US",
  AED: "en-AE",
  GBP: "en-GB",
  EUR: "en-IE",
  SGD: "en-SG",
  CAD: "en-CA",
  AUD: "en-AU"
};
function formatCurrency(value, currency = "INR", options = {}) {
  if (!Number.isFinite(value)) return "\u2014";
  return new Intl.NumberFormat(CURRENCY_LOCALE[currency], {
    style: "currency",
    currency,
    maximumFractionDigits: 0,
    ...options
  }).format(value);
}
function formatINRCompact(value) {
  if (!Number.isFinite(value)) return "\u2014";
  const abs = Math.abs(value);
  const sign = value < 0 ? "-" : "";
  if (abs >= 1e7) {
    return `${sign}\u20B9${trimZeros(abs / 1e7)} Cr`;
  }
  if (abs >= 1e5) {
    return `${sign}\u20B9${trimZeros(abs / 1e5)} L`;
  }
  if (abs >= 1e3) {
    return `${sign}\u20B9${trimZeros(abs / 1e3)} K`;
  }
  return `${sign}\u20B9${Math.round(abs)}`;
}
function formatRatePerSqft(value) {
  if (!Number.isFinite(value)) return "\u2014";
  return `${formatCurrency(Math.round(value))}/sq.ft`;
}
var INR_PER_UNIT = {
  INR: 1,
  USD: 83.4,
  AED: 22.7,
  GBP: 106.2,
  EUR: 90.5,
  SGD: 61.8,
  CAD: 61,
  AUD: 55.2
};
var FX_AS_OF = "Jul 2026";
function convertFromInr(amountInr, to) {
  if (!Number.isFinite(amountInr)) return NaN;
  const rate = INR_PER_UNIT[to] || 1;
  return amountInr / rate;
}
function trimZeros(n) {
  return n.toFixed(2).replace(/\.00$/, "").replace(/(\.\d)0$/, "$1");
}

// src/format.ts
function formatArea(value, unit = "sqft") {
  if (!Number.isFinite(value)) return "\u2014";
  const unitLabel = unit === "sqft" ? "sq.ft" : unit === "sqm" ? "sq.m" : "sq.yd";
  return `${new Intl.NumberFormat("en-IN").format(Math.round(value))} ${unitLabel}`;
}
function configSortValue(config) {
  if (config === "5+") return 5.5;
  const n = Number(config);
  return Number.isFinite(n) ? n : Number.POSITIVE_INFINITY;
}
function formatConfigRange(configs) {
  const unique = [...new Set(configs.filter((c) => Boolean(c)))].sort(
    (a, b) => configSortValue(a) - configSortValue(b)
  );
  if (unique.length === 0) return void 0;
  if (unique.length === 1) return `${unique[0]} BHK`;
  return `${unique[0]} \u2013 ${unique[unique.length - 1]} BHK`;
}

// src/finance.ts
function calculateEmi({ principal, annualRatePct, tenureMonths }) {
  const safePrincipal = Math.max(0, principal || 0);
  const months = Math.max(0, Math.floor(tenureMonths || 0));
  if (safePrincipal === 0 || months === 0) {
    return { emi: 0, totalPayable: 0, totalInterest: 0, principal: safePrincipal };
  }
  const monthlyRate = (annualRatePct || 0) / 12 / 100;
  let emi;
  if (monthlyRate <= 0) {
    emi = safePrincipal / months;
  } else {
    const factor = Math.pow(1 + monthlyRate, months);
    emi = safePrincipal * monthlyRate * factor / (factor - 1);
  }
  const totalPayable = emi * months;
  return {
    emi: round2(emi),
    totalPayable: round2(totalPayable),
    totalInterest: round2(totalPayable - safePrincipal),
    principal: safePrincipal
  };
}
function calculateAffordability(input) {
  const foir = clamp(input.foir ?? 0.5, 0, 1);
  const income = Math.max(0, input.monthlyIncome || 0);
  const obligations = Math.max(0, input.monthlyObligations ?? 0);
  const affordableEmi = Math.max(0, income * foir - obligations);
  const months = Math.max(0, Math.floor(input.tenureMonths || 0));
  const monthlyRate = (input.annualRatePct || 0) / 12 / 100;
  if (affordableEmi === 0 || months === 0) return { affordableEmi: 0, maxLoan: 0 };
  let maxLoan;
  if (monthlyRate <= 0) {
    maxLoan = affordableEmi * months;
  } else {
    const factor = Math.pow(1 + monthlyRate, months);
    maxLoan = affordableEmi * (factor - 1) / (monthlyRate * factor);
  }
  return { affordableEmi: round2(affordableEmi), maxLoan: round2(maxLoan) };
}
function calculateRentalYield({ price, monthlyRent, annualCost = 0 }) {
  const safePrice = Math.max(0, price || 0);
  const annualRent = Math.max(0, (monthlyRent || 0) * 12);
  if (safePrice === 0) {
    return { annualRent, grossYieldPct: 0, netYieldPct: 0, capRatePct: 0 };
  }
  const noi = annualRent - Math.max(0, annualCost);
  return {
    annualRent: round2(annualRent),
    grossYieldPct: round2(annualRent / safePrice * 100),
    netYieldPct: round2(noi / safePrice * 100),
    capRatePct: round2(noi / safePrice * 100)
  };
}
function projectInvestmentReturn(input) {
  const price = Math.max(0, input.price || 0);
  const years = clamp(Math.floor(input.holdingYears || 0), 1, 50);
  const escalation = Math.max(0, input.rentEscalationPct ?? 0) / 100;
  const appreciation = Math.max(0, input.appreciationPct ?? 0) / 100;
  const netYear1 = Math.max(0, (input.monthlyRent || 0) * 12 - Math.max(0, input.annualCost ?? 0));
  const rows = [];
  let cumulativeRental = 0;
  if (price === 0) {
    return {
      exitValue: 0,
      capitalGain: 0,
      totalRentalIncome: 0,
      totalReturn: 0,
      totalReturnPct: 0,
      annualizedReturnPct: 0,
      years: rows
    };
  }
  for (let year = 1; year <= years; year += 1) {
    const propertyValue = price * Math.pow(1 + appreciation, year);
    const rentalIncome = netYear1 * Math.pow(1 + escalation, year - 1);
    cumulativeRental += rentalIncome;
    const cumulativeReturn = propertyValue - price + cumulativeRental;
    rows.push({
      year,
      propertyValue: round2(propertyValue),
      rentalIncome: round2(rentalIncome),
      cumulativeRental: round2(cumulativeRental),
      cumulativeReturn: round2(cumulativeReturn)
    });
  }
  const exitValue = price * Math.pow(1 + appreciation, years);
  const capitalGain = exitValue - price;
  const totalReturn = capitalGain + cumulativeRental;
  const annualizedReturnPct = (Math.pow((price + totalReturn) / price, 1 / years) - 1) * 100;
  return {
    exitValue: round2(exitValue),
    capitalGain: round2(capitalGain),
    totalRentalIncome: round2(cumulativeRental),
    totalReturn: round2(totalReturn),
    totalReturnPct: round2(totalReturn / price * 100),
    annualizedReturnPct: round2(annualizedReturnPct),
    years: rows
  };
}
function calculateStampDuty({
  propertyValue,
  stampDutyPct = 4.9,
  registrationPct = 1,
  femaleRebatePct = 0
}) {
  const value = Math.max(0, propertyValue || 0);
  const effectiveStampDutyPct = Math.max(0, stampDutyPct - Math.max(0, femaleRebatePct));
  const stampDuty = round2(value * (effectiveStampDutyPct / 100));
  const registrationFee = round2(Math.min(value * (registrationPct / 100), 3e4));
  return { stampDuty, registrationFee, total: round2(stampDuty + registrationFee) };
}
function round2(n) {
  return Math.round((n + Number.EPSILON) * 100) / 100;
}
function clamp(n, min, max) {
  return Math.min(max, Math.max(min, n));
}

export { FX_AS_OF, INR_PER_UNIT, calculateAffordability, calculateEmi, calculateRentalYield, calculateStampDuty, convertFromInr, formatArea, formatConfigRange, formatCurrency, formatINRCompact, formatRatePerSqft, projectInvestmentReturn };
//# sourceMappingURL=index.js.map
//# sourceMappingURL=index.js.map